import { MapPin, Briefcase, DollarSign, BookOpen } from "lucide-react";
import { CardStatusTag, CardSaveButton } from "./CardHeaderActions";
import { cn, formatSalaryRange } from "@/lib/utils";
import type { CandidateProfile } from "@/types/candidates/candidates.types";
import { useAuthStore } from "@/store/authStore";
import { useToggleSaveListing } from "@/hooks/useSavedListings";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getInitials } from "@/utils";

type Props = {
  item: CandidateProfile;
};

export default function CandidateCard({ item }: Props) {
  const { user } = useAuthStore();
  const { isSaved, toggleSave } = useToggleSaveListing();
  const saved = isSaved(item.id);
  
  // Use 'any' type for images array since TS might complain if types are incomplete
  const img = (item.images as any[])?.find((img: any) => img.isPrimary)?.image?.publicUrl;

  const showStatusTag = !!user && (user.role === "headhunter" || user.role === "admin");

  return (
    <div className="fnb-card block animate-fade-up h-full flex flex-col p-4 border rounded-xl relative" style={{ borderColor: 'var(--border-c)', background: 'var(--surface-0)' }}>
      {/* Header: Avatar & Basic Info */}
      <div className="flex gap-3 mb-4 relative">
        <Avatar className="w-12 h-12 border rounded-xl shrink-0" style={{ borderColor: 'var(--border-c)' }}>
          <AvatarImage src={img || undefined} className="object-cover" />
          <AvatarFallback className="bg-orange-100 text-orange-600 font-bold rounded-xl text-sm">{getInitials(item.fullName || 'UV')}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="mb-1">
            <CardStatusTag
              show={showStatusTag}
              status={item.approvalStatus}
              label={item.approvalStatus === "approved" ? "Sẵn sàng" : "Chờ duyệt"}
            />
          </div>
          <h3 className="font-semibold text-sm truncate pr-8" style={{ color: "var(--text-1)" }}>
            {item.fullName || 'Ứng viên ẩn danh'}
          </h3>
          <p className="text-xs font-medium truncate mb-1 pr-8" style={{ color: "var(--brand)" }}>
            {item.desiredPosition || item.jobCategoryName}
          </p>
          <div className="flex items-center gap-1.5 flex-wrap pr-8">
            <span className="fnb-tag fnb-tag-brand text-[9px]">
              {item.jobType || "Full-time"}
            </span>
          </div>
        </div>
      </div>

      {/* Skills */}
      {item.skills && item.skills.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {item.skills.slice(0, 3).map((skill, idx) => (
             <span key={idx} className="fnb-tag text-[9px] bg-gray-50 border-gray-100 text-gray-600 truncate max-w-[100px]">{skill}</span>
          ))}
          {item.skills.length > 3 && (
            <span className="fnb-tag text-[9px] bg-gray-50 border-gray-100 text-gray-500">+{item.skills.length - 3}</span>
          )}
        </div>
      )}

      {/* Salary */}
      {(item.expectedSalaryMin || item.expectedSalaryMax) && (
        <div className="flex items-center gap-1.5 mb-3 text-[13px] font-semibold" style={{ color: "var(--text-1)" }}>
          <DollarSign size={14} className="text-green-600" />
          {formatSalaryRange(item.expectedSalaryMin, item.expectedSalaryMax)}
        </div>
      )}

      {/* Meta tags */}
      <div className="flex flex-wrap gap-1.5 pt-3 border-t mt-auto pr-10" style={{ borderColor: "var(--border-c)" }}>
        {item.experience !== undefined && item.experience !== null && (
          <span className="fnb-tag text-[10px]">
            <Briefcase size={9} /> KN: {item.experience} năm
          </span>
        )}
        {item.education && (
          <span className="fnb-tag text-[10px] max-w-[100px] truncate">
            <BookOpen size={9} /> {item.education}
          </span>
        )}
        {(item.province?.name || item.location) && (
          <span className="fnb-tag text-[10px] max-w-[100px] truncate">
            <MapPin size={9} /> {item.province?.name || item.location}
          </span>
        )}
      </div>

      <CardSaveButton
        className="absolute bottom-4 right-4 z-10"
        saved={saved}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggleSave(item.id, "Candidate");
        }}
      />
    </div>
  );
}
